import { useState } from "react";
import { Layout } from "@/components/layout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { TransactionTable } from "@/components/ui/transaction-table";
import { useTransactions } from "@/hooks/use-transactions";
import { AddTransactionForm } from "@/components/transactions/add-transaction-form";
import { ImportTransactions } from "@/components/transactions/import-transactions";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useSelectedBusiness } from "@/hooks/use-selected-business";
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from "@/components/ui/select";
import { useTransactionCategories } from "@/hooks/use-transactions";
import { useToast } from "@/hooks/use-toast";
import { 
  ChevronLeft, 
  ChevronRight, 
  Plus, 
  ArrowUpDown, 
  Search, 
  Download, 
  Filter,
  Sparkles,
  FileUp,
  Tag as TagIcon
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { format } from "date-fns";
import { BankSummaryCard } from "@/components/transactions/BankSummaryCard";

export default function TransactionsPage() {
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [isAddTransactionOpen, setIsAddTransactionOpen] = useState(false);
  const [isImportOpen, setIsImportOpen] = useState(false);
  const [isManualCategorizeOpen, setIsManualCategorizeOpen] = useState(false);
  const [selectedTransactions, setSelectedTransactions] = useState<number[]>([]);
  const [selectedCategoryId, setSelectedCategoryId] = useState<string>("");
  const { toast } = useToast();
  
  // Filter states
  const [searchTerm, setSearchTerm] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [selectedType, setSelectedType] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("for_review");
  
  const { categories } = useTransactionCategories();
  
  // Get transactions with filters
  // Get the active business ID from the context
  const { activeBusinessId } = useSelectedBusiness();
  
  const { 
    transactions, 
    total, 
    isLoading, 
    updateTransaction, 
    deleteTransaction,
    excludeTransaction,
    restoreTransaction,
    splitTransaction,
    categorizeTransactions,
    isPendingUpdate, 
    isPendingDelete,
    isPendingExclude,
    isPendingRestore,
    isPendingSplit,
    isPendingCategorize,
    refetch
  } = useTransactions(activeBusinessId ?? undefined, {
    page,
    limit,
    startDate,
    endDate,
    type: selectedType && selectedType !== "all" ? selectedType : undefined,
    categoryId: selectedCategory && selectedCategory !== "all" ? parseInt(selectedCategory) : undefined,
    status: selectedStatus && selectedStatus !== "all" ? selectedStatus : undefined
  });
  
  // Calculate total pages
  const totalPages = Math.ceil((total || 0) / limit);
  
  // Handle pagination
  const handleNextPage = () => {
    if (page < totalPages) {
      setPage(page + 1);
    }
  };
  
  const handlePrevPage = () => {
    if (page > 1) {
      setPage(page - 1);
    }
  };
  
  // Handle search and apply filters
  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    // Make sure the page is reset to 1 when applying new filters
    setPage(1);
    refetch();
    console.log("Applied filters:", {
      type: selectedType,
      categoryId: selectedCategory !== "all" ? parseInt(selectedCategory) : undefined,
      status: selectedStatus
    });
  };
  
  // Handle filter reset
  const resetFilters = () => {
    setSearchTerm("");
    setStartDate("");
    setEndDate("");
    setSelectedType("all");
    setSelectedCategory("all");
    setSelectedStatus("all");
    setPage(1);
  };
  
  // Handle AI categorization
  const handleAICategorize = async () => {
    if (selectedTransactions.length === 0) {
      toast({
        title: "No transactions selected",
        description: "Please select at least one transaction to categorize.",
        variant: "destructive",
      });
      return;
    }
    
    try {
      const response = await categorizeTransactions(selectedTransactions);
      
      // Parse the response data once
      const responseData = await response.json();
      
      // Check if response is OK
      if (!response.ok) {
        // Handle trial limit errors specifically
        if (responseData.error === "TRIAL_LIMIT_REACHED") {
          toast({
            title: "Trial Limit Reached",
            description: responseData.message || "You've reached your trial limit for AI categorizations. Please upgrade your plan to continue.",
            variant: "destructive",
          });
          return;
        } else if (responseData.error === "PARTIAL_TRIAL_LIMIT") {
          toast({
            title: "Trial Limit Warning",
            description: responseData.message || `You can only categorize ${responseData.remaining} more transactions with your trial.`,
            variant: "destructive",
          });
          return;
        }
        
        // Handle other errors
        throw new Error(responseData.message || "Failed to categorize transactions");
      }
      
      const result = responseData;
      
      // Show success message
      toast({
        title: "AI Categorization Complete",
        description: `Successfully categorized ${result.categorizedCount} transactions.`,
      });
      
      // If trial usage information is provided, show it
      if (result.trialUsage) {
        const { used, limit, remaining } = result.trialUsage;
        toast({
          title: "Trial Usage Updated",
          description: `You've used ${used}/${limit} AI categorizations (${remaining} remaining).`,
          variant: "default",
        });
      }
      
      // Clear selections after categorization
      setSelectedTransactions([]);
    } catch (error) {
      console.error("Error categorizing transactions:", error);
      toast({
        title: "Categorization Failed",
        description: error instanceof Error ? error.message : "There was an error categorizing the transactions. Please try again.",
        variant: "destructive",
      });
    }
  };
  
  // Handle manual categorization
  const handleManualCategorize = async () => {
    if (selectedTransactions.length === 0) {
      toast({
        title: "No transactions selected",
        description: "Please select at least one transaction to categorize.",
        variant: "destructive",
      });
      return;
    }
    
    if (!selectedCategoryId) {
      toast({
        title: "No category selected",
        description: "Please select a category to assign to the transactions.",
        variant: "destructive",
      });
      return;
    }
    
    try {
      // Update each selected transaction
      const categoryId = parseInt(selectedCategoryId);
      
      for (const transactionId of selectedTransactions) {
        await updateTransaction({
          id: transactionId,
          categoryId,
          status: "categorized"
        });
      }
      
      toast({
        title: "Manual Categorization Complete",
        description: `Successfully categorized ${selectedTransactions.length} transactions.`,
      });
      
      // Reset state
      setSelectedTransactions([]);
      setSelectedCategoryId("");
      setIsManualCategorizeOpen(false);
    } catch (error) {
      console.error("Error manually categorizing transactions:", error);
      toast({
        title: "Categorization Failed",
        description: "There was an error categorizing the transactions. Please try again.",
        variant: "destructive",
      });
    }
  };
  
  // Toggle transaction selection
  const toggleSelectTransaction = (id: number) => {
    setSelectedTransactions(prev => 
      prev.includes(id) 
        ? prev.filter(tId => tId !== id) 
        : [...prev, id]
    );
  };
  
  // Set default dates if not set
  const getDefaultStartDate = () => {
    if (!startDate) {
      const date = new Date();
      date.setMonth(date.getMonth() - 1);
      return format(date, "yyyy-MM-dd");
    }
    return startDate;
  };
  
  const getDefaultEndDate = () => {
    if (!endDate) {
      return format(new Date(), "yyyy-MM-dd");
    }
    return endDate;
  };

  return (
    <Layout>
      <div className="px-4 py-6 sm:px-6 lg:px-8">
        {/* Page header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-neutral-900">Transactions</h1>
            <p className="mt-1 text-sm text-neutral-500">
              Manage and view your transaction history
            </p>
          </div>
          <div className="mt-4 md:mt-0 flex flex-col sm:flex-row gap-3">
            <Button 
              variant="outline" 
              onClick={() => setIsImportOpen(true)}
            >
              <FileUp className="h-4 w-4 mr-2" />
              Import
            </Button>
            <Button 
              onClick={() => setIsAddTransactionOpen(true)}
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Transaction
            </Button>
            <div className="flex gap-2">
              <Button 
                variant="outline"
                onClick={() => setIsManualCategorizeOpen(true)} 
                disabled={selectedTransactions.length === 0}
              >
                <TagIcon className="h-4 w-4 mr-2" />
                Manual Categorize
                {selectedTransactions.length > 0 && ` (${selectedTransactions.length})`}
              </Button>
              <Button 
                variant="secondary"
                onClick={handleAICategorize} 
                disabled={selectedTransactions.length === 0 || isPendingCategorize}
              >
                <Sparkles className="h-4 w-4 mr-2" />
                {isPendingCategorize ? "Categorizing..." : "AI Categorize"}
                {selectedTransactions.length > 0 && ` (${selectedTransactions.length})`}
              </Button>
            </div>
          </div>
        </div>
        
        {/* Bank Account Summary */}
        <BankSummaryCard pendingCount={transactions.filter((t: any) => t.status === 'pending').length} />

        {/* Filters */}
        <Card className="mb-6">
          <CardHeader className="px-4 py-3 border-b">
            <CardTitle className="text-md flex items-center">
              <Filter className="h-4 w-4 mr-2" />
              Filters
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4">
            <form onSubmit={handleSearch} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
              {/* Search */}
              <div className="space-y-2">
                <Label htmlFor="search">Search</Label>
                <div className="relative">
                  <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-neutral-400" />
                  <Input
                    id="search"
                    placeholder="Search description"
                    className="pl-9"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>
              </div>
              
              {/* Date Range */}
              <div className="space-y-2">
                <Label htmlFor="startDate">Start Date</Label>
                <Input
                  id="startDate"
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="endDate">End Date</Label>
                <Input
                  id="endDate"
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                />
              </div>
              
              {/* Type Filter */}
              <div className="space-y-2">
                <Label htmlFor="type">Type</Label>
                <Select value={selectedType} onValueChange={setSelectedType}>
                  <SelectTrigger id="type">
                    <SelectValue placeholder="All Types" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Types</SelectItem>
                    <SelectItem value="income">Income</SelectItem>
                    <SelectItem value="expense">Expense</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              {/* Category Filter */}
              <div className="space-y-2">
                <Label htmlFor="category">Category</Label>
                <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                  <SelectTrigger id="category">
                    <SelectValue placeholder="All Categories" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Categories</SelectItem>
                    {categories.map((category) => (
                      <SelectItem key={category.id} value={category.id.toString()}>
                        {category.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {/* Status Filter */}
              <div className="space-y-2 xl:col-span-2">
                <Label htmlFor="status">Status</Label>
                <Select value={selectedStatus} onValueChange={setSelectedStatus}>
                  <SelectTrigger id="status">
                    <SelectValue placeholder="All Statuses" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Statuses</SelectItem>
                    <SelectItem value="for_review">For Review</SelectItem>
                    <SelectItem value="reviewed">Reviewed</SelectItem>
                    <SelectItem value="excluded">Excluded</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              {/* Filter Actions */}
              <div className="flex items-end gap-2 xl:col-span-3">
                <Button type="submit" variant="default">
                  Apply Filters
                </Button>
                <Button type="button" variant="outline" onClick={resetFilters}>
                  Reset
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
        
        {/* Quick Status Filters */}
        <div className="flex gap-2 mb-4">
          {[
            { label: "For Review", value: "for_review" },
            { label: "Reviewed", value: "reviewed" },
            { label: "Excluded", value: "excluded" },
          ].map((btn) => (
            <Button
              key={btn.value}
              variant={selectedStatus === btn.value ? "default" : "outline"}
              size="sm"
              onClick={() => {
                setSelectedStatus(btn.value);
                setPage(1);
              }}
            >
              {btn.label}
            </Button>
          ))}
          {selectedStatus !== "all" && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => { setSelectedStatus("all"); setPage(1); }}
            >
              Show All
            </Button>
          )}
        </div>

        {/* Transaction Table */}
        <Card>
          <CardHeader className="px-4 py-3 border-b">
            <div className="flex justify-between items-center">
              <CardTitle className="text-md flex items-center">
                <ArrowUpDown className="h-4 w-4 mr-2" />
                Transactions
              </CardTitle>
              
              {/* Show records count */}
              <div className="text-sm text-neutral-500">
                {transactions && transactions.length > 0
                  ? `Showing ${(page - 1) * limit + 1} - ${Math.min(
                      page * limit,
                      total || 0
                    )} of ${total || 0} records`
                  : "No records found"}
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            <TransactionTable 
              transactions={transactions || []}
              onEdit={(id, data) => updateTransaction({id, ...data})}
              onDelete={deleteTransaction}
              onExclude={excludeTransaction}
              onRestore={restoreTransaction}
              onSelect={toggleSelectTransaction}
              selectedTransactions={selectedTransactions}
              isLoading={isLoading}
              isPendingEdit={isPendingUpdate}
              isPendingDelete={isPendingDelete}
              isPendingExclude={isPendingExclude}
              isPendingRestore={isPendingRestore}
              isPendingSplit={isPendingSplit}
            />
            
            {/* Pagination */}
            {totalPages > 0 && (
              <div className="flex items-center justify-between px-4 py-4 border-t border-neutral-200">
                <div className="flex items-center">
                  <Label htmlFor="limit" className="mr-2 text-sm">Show</Label>
                  <Select
                    value={limit.toString()}
                    onValueChange={(value) => {
                      setLimit(parseInt(value));
                      setPage(1);
                    }}
                  >
                    <SelectTrigger id="limit" className="w-20">
                      <SelectValue placeholder={limit.toString()} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="5">5</SelectItem>
                      <SelectItem value="10">10</SelectItem>
                      <SelectItem value="25">25</SelectItem>
                      <SelectItem value="50">50</SelectItem>
                    </SelectContent>
                  </Select>
                  <span className="ml-2 text-sm text-neutral-500">entries</span>
                </div>
                
                <div className="flex items-center space-x-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handlePrevPage}
                    disabled={page === 1}
                  >
                    <ChevronLeft className="h-4 w-4" />
                    <span className="ml-1">Previous</span>
                  </Button>
                  <div className="text-sm">
                    Page {page} of {totalPages}
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleNextPage}
                    disabled={page === totalPages}
                  >
                    <span className="mr-1">Next</span>
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
      
      {/* Add Transaction Dialog */}
      <AddTransactionForm 
        open={isAddTransactionOpen} 
        onOpenChange={setIsAddTransactionOpen} 
        onSuccess={() => {
          setIsAddTransactionOpen(false);
          refetch();
        }}
      />
      
      {/* Import Transactions Dialog */}
      <ImportTransactions 
        open={isImportOpen} 
        onOpenChange={setIsImportOpen}
        onSuccess={() => {
          setIsImportOpen(false);
          refetch();
        }}
      />
      
      {/* Manual Categorize Dialog */}
      <Dialog open={isManualCategorizeOpen} onOpenChange={setIsManualCategorizeOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Manually Categorize Transactions</DialogTitle>
            <DialogDescription>
              Select a category to assign to {selectedTransactions.length} selected transaction{selectedTransactions.length !== 1 ? 's' : ''}.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="manual-category">Category</Label>
              <Select value={selectedCategoryId} onValueChange={setSelectedCategoryId}>
                <SelectTrigger id="manual-category">
                  <SelectValue placeholder="Select a category" />
                </SelectTrigger>
                <SelectContent>
                  {categories.map((category) => (
                    <SelectItem key={category.id} value={category.id.toString()}>
                      {category.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsManualCategorizeOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleManualCategorize} 
              disabled={!selectedCategoryId || isPendingUpdate}
            >
              {isPendingUpdate ? "Categorizing..." : "Categorize Transactions"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
